import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaCrown, FaSpinner, FaCheckCircle, FaInfoCircle, FaBolt } from 'react-icons/fa';
import { toast } from 'sonner';
import UpgradeModal from './UpgradeModal';
import { getErrorMessage } from '../utils/errorMessage';
import { useAuth } from '../context/AuthContext';
import {
  SUBSCRIPTIONS,
  SUBSCRIPTION_TYPES,
  getSubscriptionType,
} from '../config/subscriptions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Subscription / Pro section for SettingsPage.
 * Shows current plan, next billing date, and cancel/reactivate controls.
 */
const SubscriptionSection = () => {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const authHeader = () => {
    const t = localStorage.getItem('session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/billing/subscription`, {
        headers: authHeader(), withCredentials: true,
      });
      setSub(data);
    } catch (err) {
      const status = err.response?.status;
      console.error('[SubscriptionSection] load failed:', status, err.response?.data);
      // 401 = not logged in (hide section). Other errors = show retry.
      if (status === 401) {
        setSub(null);
        setLoadError('unauthenticated');
      } else {
        // Always coerce to string — FastAPI 422 returns detail as a list of
        // Pydantic error objects which would crash React when rendered.
        setLoadError(getErrorMessage(err, 'Kunne ikke hente abonnement'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async () => {
    const tierName = getSubscriptionType(user) === SUBSCRIPTION_TYPES.LIGHT ? 'Light' : 'Pro';
    if (!window.confirm(`Er du sikker på, at du vil opsige dit ${tierName}-abonnement? Du beholder adgangen indtil periodens udløb.`)) return;
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/billing/cancel`, {}, {
        headers: authHeader(), withCredentials: true,
      });
      setSub(data);
      toast.success(`Abonnement opsagt. Du beholder ${tierName} indtil udløbsdatoen.`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Kunne ikke opsige'));
    } finally {
      setActing(false);
    }
  };

  const handleReactivate = async () => {
    setActing(true);
    try {
      const { data } = await axios.post(`${API}/billing/reactivate`, {}, {
        headers: authHeader(), withCredentials: true,
      });
      setSub(data);
      toast.success('Abonnement genoptaget.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Kunne ikke genoptage'));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="subscription-section-loading">
        <div className="flex items-center gap-3 text-gray-500">
          <FaSpinner className="animate-spin" />
          <span>Henter abonnement...</span>
        </div>
      </div>
    );
  }

  // 401 → user really isn't logged in. Hide the whole section.
  if (loadError === 'unauthenticated') return null;

  // Other load errors → show a retry box so the user knows something went wrong.
  if (loadError) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="subscription-section-error">
        <div className="flex items-center gap-2 mb-3">
          <FaCrown className="text-yellow-500" />
          <h2 className="text-2xl font-bold">Pro-abonnement</h2>
        </div>
        <p className="text-red-600 text-sm mb-3">Kunne ikke hente abonnementsoplysninger: {loadError}</p>
        <button
          onClick={load}
          data-testid="subscription-retry-btn"
          className="bg-gray-100 text-gray-800 font-medium py-2 px-4 rounded-lg hover:bg-gray-200"
        >
          Prøv igen
        </button>
      </div>
    );
  }

  // No data at all → render upgrade CTA so the section is never invisible
  // when the user is logged in.
  if (!sub) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="subscription-section">
        <div className="flex items-center gap-2 mb-3">
          <FaCrown className="text-yellow-500" />
          <h2 className="text-2xl font-bold">Pro-abonnement</h2>
        </div>
        <p className="text-gray-600 mb-4">Du har ikke et aktivt Pro-abonnement endnu.</p>
        <button
          data-testid="open-upgrade-modal-btn"
          onClick={() => setShowUpgrade(true)}
          className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-3 px-6 rounded-lg hover:from-yellow-500 hover:to-orange-600"
        >
          Køb Pro-adgang
        </button>
        <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} reason="general" currentUser={user}
        requiredTier="light"
      />
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (_e) { return iso; }
  };

  // Determine the user's actual tier (light | pro | free) from AuthContext.
  // Kilde-til-sandhed er `getSubscriptionType(user)` — Stripe `pro_plan` bruges
  // kun til at vise pris/interval label under overskriften.
  const tier = getSubscriptionType(user);
  const isLight = tier === SUBSCRIPTION_TYPES.LIGHT;
  const tierConfig = SUBSCRIPTIONS[tier] || SUBSCRIPTIONS.pro;

  const planLabel = sub.pro_plan === 'monthly'
    ? { name: 'Månedlig plan', price: '49 kr/md' }
    : sub.pro_plan === 'yearly' ? { name: 'Årlig plan', price: '399 kr/år' }
    : sub.pro_plan === 'light_monthly' ? { name: 'Light månedlig', price: '25 kr/md' }
    : (sub.pro_plan && sub.pro_plan.startsWith('manual_'))
      ? { name: 'Gratis Pro (kampagne)', price: '' }
      : { name: tierConfig.name, price: '' };

  // Tier-specific visual + copy so Light-users don't see Pro branding.
  const tierTheme = isLight
    ? {
        // Blue accent for Light so it's visually distinct from Pro's gold.
        cardBorder: 'border-sky-200',
        cardBg: 'bg-gradient-to-br from-sky-50 via-white to-blue-50',
        glow: 'bg-gradient-to-br from-sky-300/30 to-blue-400/20',
        iconBg: 'bg-gradient-to-br from-sky-400 to-blue-500',
        Icon: FaBolt,
        headingAccent: 'bg-gradient-to-r from-sky-500 to-blue-500 bg-clip-text text-transparent',
        keyBoxBorder: 'border-sky-200',
        buttonGradient: 'from-sky-400 to-blue-500 hover:from-sky-500 hover:to-blue-600',
      }
    : {
        cardBorder: 'border-yellow-200',
        cardBg: 'bg-gradient-to-br from-yellow-50 via-white to-orange-50',
        glow: 'bg-gradient-to-br from-yellow-300/30 to-orange-400/20',
        iconBg: 'bg-gradient-to-br from-yellow-400 to-orange-500',
        Icon: FaCrown,
        headingAccent: 'bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent',
        keyBoxBorder: 'border-yellow-200',
        buttonGradient: 'from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600',
      };

  // Features to show: pull from subscription config so Light and Pro always
  // match the "single source of truth" defined in config/subscriptions.js.
  const features = (tierConfig.features && tierConfig.features.length > 0)
    ? tierConfig.features
    : [
        'Ubegrænset adgang til alle opskrifter',
        'Gem favoritter og indkøbslister',
        'Opret og del egne opskrifter',
        'Prioriteret support',
      ];

  // No active subscription (guest or expired) — keep simple upgrade CTA
  if (!sub.has_pro_access && sub.pro_status !== 'cancelled') {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="subscription-section">
        <div className="flex items-center gap-2 mb-3">
          <FaCrown className="text-yellow-500" />
          <h2 className="text-2xl font-bold">Pro-abonnement</h2>
        </div>
        <p className="text-gray-600 mb-4">
          Du har ikke et aktivt Pro-abonnement. Opgradér for ubegrænset adgang til alle opskrifter.
        </p>
        <button
          data-testid="open-upgrade-modal-btn"
          onClick={() => setShowUpgrade(true)}
          className="w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-3 px-6 rounded-lg hover:from-yellow-500 hover:to-orange-600"
        >
          Køb Pro-adgang
        </button>
        <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} reason="general" currentUser={user}
        requiredTier="light"
      />
      </div>
    );
  }

  // Premium "membership card" for active & cancel-pending users
  const isCancelled = sub.cancel_at_period_end;
  const isManualGrant = sub.pro_grant_source === 'admin_manual' ||
    (typeof sub.pro_plan === 'string' && sub.pro_plan.startsWith('manual_'));

  return (
    <div
      data-testid="subscription-section"
      data-tier={tier}
      className={`relative overflow-hidden rounded-2xl shadow-lg border transition-shadow hover:shadow-xl ${
        isCancelled
          ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50'
          : `${tierTheme.cardBorder} ${tierTheme.cardBg}`
      }`}
    >
      {/* Subtle corner glow */}
      <div className={`pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-2xl ${tierTheme.glow}`}></div>

      <div className="relative p-6 sm:p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${tierTheme.iconBg}`}>
              <tierTheme.Icon className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight" data-testid="sub-heading">
                SlushBook <span className={tierTheme.headingAccent}>{tierConfig.name}</span>
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {planLabel.name}
                {planLabel.price && <span> · {planLabel.price}</span>}
              </p>
            </div>
          </div>
          {/* Status pill */}
          {isManualGrant ? (
            <span
              data-testid="sub-status"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200"
            >
              <span className="text-base leading-none">🎁</span>
              Gratis Pro
            </span>
          ) : !isCancelled ? (
            <span
              data-testid="sub-status"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              Aktiv
            </span>
          ) : (
            <span
              data-testid="sub-status"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
            >
              <FaInfoCircle className="text-amber-600" /> Opsagt
            </span>
          )}
        </div>

        {/* Key date highlight */}
        <div className={`rounded-xl border p-4 mb-6 ${
          isManualGrant ? 'bg-white/70 border-orange-200' :
          isCancelled ? 'bg-white/70 border-amber-200' : `bg-white/70 ${tierTheme.keyBoxBorder}`
        }`}>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
            {isManualGrant ? 'Gratis Pro-adgang indtil' :
             isCancelled ? 'Adgang fortsætter indtil' : 'Næste betaling'}
          </p>
          <p className="text-xl font-bold text-gray-900" data-testid="sub-period-end">
            {formatDate(sub.pro_current_period_end)}
          </p>
          <input type="hidden" data-testid="sub-plan" value={planLabel.name} />
        </div>

        {isManualGrant ? (
          <>
            {/* Features list */}
            <ul className="space-y-2.5 mb-6">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-gray-700">
                  <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* Gift callout */}
            <div className="rounded-xl bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 p-4 mb-4">
              <p className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <span className="text-base">🎁</span> Du har fået gratis Pro-adgang
              </p>
              <p className="text-sm text-gray-700">
                {sub.pro_grant_note ? sub.pro_grant_note : 'Velkommen som en af de første brugere!'}<br />
                Adgangen er gratis og fornyes ikke automatisk — den udløber stille og roligt på ovenstående dato.
              </p>
            </div>

            <p className="text-xs text-center text-gray-500">
              Når kampagnen udløber kan du opgradere til et almindeligt Pro-abonnement.
            </p>
          </>
        ) : isCancelled ? (
          <>
            <div className="mb-5">
              <p className="text-gray-700 mb-3">
                Dit abonnement er opsagt — men du beholder alle Pro-funktioner indtil ovenstående dato.
              </p>
              <p className="text-sm text-gray-600 mb-3">Du kan stadig:</p>
              <ul className="space-y-2">
                {features.slice(0, 3).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                    <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-white/60 border border-orange-100 p-4 mb-5">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Vi håber du kommer tilbage</span> <span className="text-red-500">❤️</span>
                <br />
                SlushBook bliver bedre for hver bruger der bidrager med idéer, opskrifter og erfaringer.
              </p>
            </div>
            <button
              data-testid="reactivate-subscription-btn"
              onClick={handleReactivate}
              disabled={acting}
              className={`w-full sm:w-auto bg-gradient-to-r ${tierTheme.buttonGradient} text-white font-bold py-3 px-6 rounded-lg disabled:opacity-60 transition-all shadow-md hover:shadow-lg active:scale-95`}
            >
              {acting ? 'Genoptager...' : 'Genoptag abonnement'}
            </button>
          </>
        ) : (
          <>
            {/* Features list */}
            <ul className="space-y-2.5 mb-6">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-gray-700">
                  <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {/* Community callout */}
            <div className="rounded-xl bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-100 p-4 mb-6">
              <p className="text-sm font-semibold text-gray-900 mb-1">
                💡 Hjælp med at gøre SlushBook endnu bedre
              </p>
              <p className="text-sm text-gray-700">
                Del dine egne opskrifter, tips, tricks og erfaringer med andre slush-entusiaster. Jo flere der bidrager, jo bedre bliver fællesskabet <span className="text-red-500">❤️</span>
              </p>
            </div>

            {/* Thank-you line */}
            <p className="text-xs text-center text-gray-500 mb-4">
              Tak fordi du støtter SlushBook <span className="text-red-500">❤️</span>
            </p>

            <button
              data-testid="cancel-subscription-btn"
              onClick={handleCancel}
              disabled={acting}
              className="w-full sm:w-auto text-sm text-gray-500 hover:text-red-600 font-medium py-2 px-4 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-60"
            >
              {acting ? 'Opsiger...' : 'Opsig abonnement'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSection;
